import api from './api';

const API_BASE_URL = '/v1/rsoc-keywords';

export interface KeywordStats {
  keyword: string;
  category: string | null;
  total_revenue: number;
  total_clicks: number;
  avg_rpc: number;
  days_active: number;
  campaign_count: number;
  ad_count: number;
  daily: Array<{
    date: string;
    revenue: number;
    clicks: number;
    rpc: number;
  }>;
}

export interface CategoryBreakdown {
  category: string | null;
  total_revenue: number;
  total_clicks: number;
  avg_rpc: number;
  keyword_count: number;
  days_active: number;
}

export interface CampaignBreakdown {
  campaign_id: string;
  campaign_name: string | null;
  total_revenue: number;
  total_clicks: number;
  avg_rpc: number;
  days_active: number;
  keyword_count: number;
  ad_count: number;
}

export interface AdBreakdown {
  ad_id: string;
  campaign_id: string;
  ad_name: string | null;
  campaign_name: string | null;
  ad_image_url: string | null;
  total_revenue: number;
  total_clicks: number;
  avg_rpc: number;
  days_active: number;
  keyword_count: number;
}

class RsocService {
  /**
   * Get keyword-level statistics with daily breakdown
   */
  async getKeywordStats(
    startDate: string,
    endDate: string,
    search?: string,
  ): Promise<KeywordStats[]> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    if (search) {
      params.append('search', search);
    }

    const response = await api.get(`${API_BASE_URL}/stats?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Get category breakdown
   */
  async getCategoryBreakdown(
    startDate: string,
    endDate: string,
  ): Promise<CategoryBreakdown[]> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });

    const response = await api.get(
      `${API_BASE_URL}/category-breakdown?${params.toString()}`,
    );
    return response.data.data;
  }

  /**
   * Get keywords for a specific category
   */
  async getCategoryKeywords(
    category: string,
    startDate: string,
    endDate: string,
  ): Promise<any[]> {
    const params = new URLSearchParams({
      category,
      start_date: startDate,
      end_date: endDate,
    });

    const response = await api.get(
      `${API_BASE_URL}/category-keywords?${params.toString()}`,
    );
    return response.data.data;
  }

  /**
   * Get daily breakdown for keyword in category
   */
  async getCategoryKeywordDaily(
    category: string,
    keyword: string,
    startDate: string,
    endDate: string,
  ): Promise<any[]> {
    const params = new URLSearchParams({
      category,
      keyword,
      start_date: startDate,
      end_date: endDate,
    });

    const response = await api.get(
      `${API_BASE_URL}/category-keyword-daily?${params.toString()}`,
    );
    return response.data.data;
  }

  /**
   * Get campaigns
   */
  async getCampaigns(startDate: string, endDate: string): Promise<CampaignBreakdown[]> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });

    const response = await api.get(`${API_BASE_URL}/campaigns?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Get keywords for a specific campaign
   */
  async getCampaignKeywords(
    campaignId: string,
    startDate: string,
    endDate: string,
  ): Promise<any[]> {
    const params = new URLSearchParams({
      campaign_id: campaignId,
      start_date: startDate,
      end_date: endDate,
    });

    const response = await api.get(
      `${API_BASE_URL}/campaign-keywords?${params.toString()}`,
    );
    return response.data.data;
  }

  /**
   * Get daily breakdown for keyword in campaign
   */
  async getCampaignKeywordDaily(
    campaignId: string,
    keyword: string,
    startDate: string,
    endDate: string,
  ): Promise<any[]> {
    const params = new URLSearchParams({
      campaign_id: campaignId,
      keyword,
      start_date: startDate,
      end_date: endDate,
    });

    const response = await api.get(
      `${API_BASE_URL}/campaign-keyword-daily?${params.toString()}`,
    );
    return response.data.data;
  }

  /**
   * Get ads
   */
  async getAds(startDate: string, endDate: string): Promise<AdBreakdown[]> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });

    const response = await api.get(`${API_BASE_URL}/ads?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Get keywords for a specific ad
   */
  async getAdKeywords(
    adId: string,
    startDate: string,
    endDate: string,
  ): Promise<any[]> {
    const params = new URLSearchParams({
      ad_id: adId,
      start_date: startDate,
      end_date: endDate,
    });

    const response = await api.get(
      `${API_BASE_URL}/ad-keywords?${params.toString()}`,
    );
    return response.data.data;
  }

  /**
   * Get daily breakdown for keyword in ad
   */
  async getAdKeywordDaily(
    adId: string,
    keyword: string,
    startDate: string,
    endDate: string,
  ): Promise<any[]> {
    const params = new URLSearchParams({
      ad_id: adId,
      keyword,
      start_date: startDate,
      end_date: endDate,
    });

    const response = await api.get(
      `${API_BASE_URL}/ad-keyword-daily?${params.toString()}`,
    );
    return response.data.data;
  }
}

export default new RsocService();
